import type { AttemptIdempotencyKey } from '@sportpark/shared/operation-attempt';
import { type ClubNow, isWallClockPast } from '~/shared/lib/clubClock';
import { http } from './http-client';

import type {
  CancelTournamentResponse,
  CreateTournamentResponse,
  FinishTournamentResponse,
  GenerateBracketResponse,
  MarkTournamentAttendanceResponse,
  PrizePayout,
  RescheduleTournamentBody,
  RescheduleTournamentResponse,
  ReopenPreviewResponse,
  ReopenTournamentResponse,
  ResetBracketResponse,
  LeaderboardEntryDto as GenLeaderboardEntryDto,
  LeaderboardRowDto as GenLeaderboardRowDto,
  TournamentCoachDto,
} from './generated';

export type {
  CancelTournamentResponse,
  GenerateBracketResponse,
  RescheduleTournamentBody,
  RescheduleTournamentResponse,
  ReopenPreviewResponse,
  ReopenTournamentResponse,
  ResetBracketResponse,
};

export type LeaderboardEntry = GenLeaderboardEntryDto;

export type LeaderboardRow = GenLeaderboardRowDto;

import type { SportCode } from './sports-api';
import { PrizeDestination, type ScoringMode, type TournamentFormat, TournamentStatus } from './generated';
import type {
  TennisSetScore as TournamentMatchSet,
  PrizeShareBody as TournamentPrizeShare,
} from './generated';
import type {
  ClientsLookupResponse,
  CreateTournamentBody,
  SubmitMatchScoreBody,
  SubmitMatchScoreResponse,
  UpdateTournamentBody,
  UpdateTournamentResponse,
  MatchDto,
  RegistrationDto,
  RoundDto,
  TournamentDetailDto,
  TournamentDetailResponse,
} from './generated';
import type {
  AutoPickCourtsResponse,
  PlanTournamentBody,
  PutTournamentNormsBody,
  RegisterForTournamentBody,
  RegisterForTournamentResponse,
  TournamentNormsResponse,
  TournamentPlanDto,
  TournamentSummary,
  UnregisterFromTournamentResponse,
} from './generated';

export type {
  PlanTournamentBody,
  PutTournamentNormsBody,
  ScoringMode,
  TournamentNormsResponse,
  TournamentPlanDto,
  TournamentDetailDto,
  TournamentFormat,
  MatchDto,
  TournamentMatchSet,
  TournamentPrizeShare,
  RegistrationDto,
  RoundDto,
  TournamentStatus,
};

export const effectiveTournamentStatus = (
  t: Pick<TournamentDetailDto, 'status' | 'effectiveStatus' | 'registrationClosesAt'>,
  now: ClubNow,
): TournamentStatus => {
  if (t.effectiveStatus !== 'registrationOpen') return t.effectiveStatus;
  if (t.registrationClosesAt && isWallClockPast(t.registrationClosesAt, now))
    return TournamentStatus.REGISTRATION_CLOSED;
  return t.effectiveStatus;
};

export const isArchivedTournament = (t: Pick<TournamentDetailDto, 'isArchived'>): boolean => t.isArchived;

export type TournamentListItem = TournamentSummary;

export interface CancelledRegistrationRefund {
  registrationId: string;
  clientId: string;
  refundedAmountKopecks: number;
}

export type ConflictResolution =
  | { bookingId: string; action: 'reschedule'; targetCourtId: string }
  | { bookingId: string; action: 'cancel' };

export type CreateTournamentRequest = CreateTournamentBody;

export const prizeDestinationOf = (value: 'advance' | 'bonus'): PrizeDestination =>
  value === 'bonus' ? PrizeDestination.BONUS : PrizeDestination.ADVANCE;

export type { CreateTournamentResponse };

export type UpdateTournamentRequest = UpdateTournamentBody;

export type RegisterRequest = RegisterForTournamentBody;
export type RegisterResponse = RegisterForTournamentResponse;
export type UnregisterResponse = UnregisterFromTournamentResponse;

export interface TennisSetSubmission {
  gamesA: number;
  gamesB: number;
  tieBreakA?: number;
  tieBreakB?: number;
}

export type SubmitScoreRequest = SubmitMatchScoreBody;

export type SubmitScoreResponse = SubmitMatchScoreResponse;

export type { FinishTournamentResponse, PrizePayout };

export interface AutoPickRequest {
  sport: SportCode;
  startAt: string;
  endAt: string;
  count: number;
}

export type AutoPickResponse = AutoPickCourtsResponse;

export interface AvailabilityRequest {
  displacedBookingIds: string[];
  excludedCourtIds: string[];
}

export interface AvailabilityItem {
  bookingId: string;
  alternatives: Array<{
    courtId: string;
    courtName: string;
    score: number;
    sameType: boolean;
    adjacent: boolean;
  }>;
}

export interface AvailabilityResponse {
  items: AvailabilityItem[];
}

export const tournamentApi = {
  list(call?: { signal?: AbortSignal }): Promise<{ items: TournamentListItem[] }> {
    return http.get<{ items: TournamentListItem[] }>('/tournaments', { signal: call?.signal });
  },

  clientsLookup(q: string, limit = 10, call?: { signal?: AbortSignal }): Promise<ClientsLookupResponse> {
    return http.get<ClientsLookupResponse>('/tournaments/clients-lookup', { query: { q, limit }, signal: call?.signal });
  },

  detail(id: string, call?: { signal?: AbortSignal }): Promise<TournamentDetailResponse> {
    return http.get<TournamentDetailResponse>(`/tournaments/${id}`, { signal: call?.signal });
  },

  create(payload: CreateTournamentRequest): Promise<CreateTournamentResponse> {
    return http.post<CreateTournamentResponse>('/tournaments', payload);
  },

  update(id: string, payload: UpdateTournamentRequest): Promise<UpdateTournamentResponse> {
    return http.patch<UpdateTournamentResponse>(`/tournaments/${id}`, payload);
  },

  assignCoach(id: string, trainerId: string | null): Promise<TournamentCoachDto> {
    return http.put<TournamentCoachDto>(`/tournaments/${id}/coach`, { trainerId });
  },

  plan(body: PlanTournamentBody, call?: { signal?: AbortSignal }): Promise<TournamentPlanDto> {
    return http.post<TournamentPlanDto>('/tournaments/plan', body, { signal: call?.signal });
  },

  norms(call?: { signal?: AbortSignal }): Promise<TournamentNormsResponse> {
    return http.get<TournamentNormsResponse>('/tournaments/norms', { signal: call?.signal });
  },

  saveNorms(body: PutTournamentNormsBody): Promise<TournamentNormsResponse> {
    return http.put<TournamentNormsResponse>('/tournaments/norms', body);
  },

  autoPick(req: AutoPickRequest): Promise<AutoPickResponse> {
    return http.post<AutoPickResponse>('/schedule/auto-pick-courts', req);
  },

  findAvailability(req: AvailabilityRequest): Promise<AvailabilityResponse> {
    return http.post<AvailabilityResponse>('/schedule/find-availability', req);
  },

  reschedule(id: string, payload: RescheduleTournamentBody): Promise<RescheduleTournamentResponse> {
    return http.patch<RescheduleTournamentResponse>(`/tournaments/${id}/reschedule`, payload);
  },

  resetBracket(id: string): Promise<ResetBracketResponse> {
    return http.post<ResetBracketResponse>(`/tournaments/${id}/bracket/reset`);
  },

  reopenPreview(id: string, call?: { signal?: AbortSignal }): Promise<ReopenPreviewResponse> {
    return http.get<ReopenPreviewResponse>(`/tournaments/${id}/reopen/preview`, { signal: call?.signal });
  },

  reopen(id: string, reason?: string): Promise<ReopenTournamentResponse> {
    return http.post<ReopenTournamentResponse>(`/tournaments/${id}/reopen`, { reason });
  },

  cancel(id: string): Promise<CancelTournamentResponse> {
    return http.post<CancelTournamentResponse>(`/tournaments/${id}/cancel`, {});
  },

  register(id: string, payload: RegisterRequest, idempotencyKey: AttemptIdempotencyKey): Promise<RegisterResponse> {
    return http.post<RegisterResponse>(`/tournaments/${id}/register`, payload, { idempotencyKey });
  },

  unregister(id: string, registrationId: string): Promise<UnregisterResponse> {
    return http.delete<UnregisterResponse>(`/tournaments/${id}/register/${registrationId}`);
  },

  markAttendance(
    id: string,
    registrationId: string,
    attendance: 'attended' | 'noShow',
  ): Promise<MarkTournamentAttendanceResponse> {
    return http.post<MarkTournamentAttendanceResponse>(
      `/tournaments/${id}/registrations/${registrationId}/attendance`,
      { attendance },
    );
  },

  closeRegistration(id: string): Promise<void> {
    return http.post<void>(`/tournaments/${id}/close-registration`, {});
  },

  generateBracket(id: string): Promise<GenerateBracketResponse> {
    return http.post<GenerateBracketResponse>(`/tournaments/${id}/generate-bracket`, {});
  },

  submitMatchScore(
    tournamentId: string,
    matchId: string,
    payload: SubmitScoreRequest,
  ): Promise<SubmitScoreResponse> {
    return http.post<SubmitScoreResponse>(
      `/tournaments/${tournamentId}/matches/${matchId}/result`,
      payload,
    );
  },

  finish(id: string): Promise<FinishTournamentResponse> {
    return http.post<FinishTournamentResponse>(`/tournaments/${id}/finish`, {});
  },

  leaderboard(id: string, call?: { signal?: AbortSignal }): Promise<{ tournamentId: string; leaderboard: LeaderboardEntry[] }> {
    return http.get<{ tournamentId: string; leaderboard: LeaderboardEntry[] }>(
      `/tournaments/${id}/leaderboard`, { signal: call?.signal },
    );
  },
};
